import React from 'react';
import {
  FiActivity,
  FiCheckCircle,
  FiAlertTriangle,
  FiAlertOctagon,
} from 'react-icons/fi';

import { useAgentHealthScore } from '../../hooks/useAgentAnalytics';
import type { AnalyticsDateRange, AnalyticsPeriod } from '../../services/agentAnalyticsApi';

interface HealthScoreHeroProps {
  agentId: string;
  period: AnalyticsPeriod;
  /** Intervalo personalizado — substitui a janela do `period` */
  range?: AnalyticsDateRange;
}

interface InterpretationConfig {
  label: string;
  description: string;
  color: string;
  icon: React.ReactNode;
}

/**
 * Traduz um score 0-1 em uma interpretação leiga para o cliente.
 *
 * Os limiares (0.85 / 0.7) foram escolhidos a partir do histórico do
 * agente LIA SDR TROIA (que fica tipicamente entre 0.75-0.85 em produção)
 * — ajustar conforme calibração futura.
 */
function interpret(score: number): InterpretationConfig {
  if (score >= 0.85) {
    return {
      label: 'Seu agente está excelente',
      description:
        'As conversas estão indo bem e os avaliadores estão satisfeitos com as respostas.',
      color: '#10b981',
      icon: <FiCheckCircle className="h-8 w-8" />,
    };
  }
  if (score >= 0.7) {
    return {
      label: 'Bom, mas tem pontos de melhoria',
      description:
        'O agente está cumprindo sua função, mas há detalhes que podem ser ajustados para melhorar a qualidade.',
      color: '#f59e0b',
      icon: <FiAlertTriangle className="h-8 w-8" />,
    };
  }
  return {
    label: 'Precisa de atenção',
    description:
      'Alguns pontos importantes precisam ser revisados. Veja as sugestões abaixo para melhorar.',
    color: '#ef4444',
    icon: <FiAlertOctagon className="h-8 w-8" />,
  };
}

export const HealthScoreHero: React.FC<HealthScoreHeroProps> = ({
  agentId,
  period,
  range,
}) => {
  const { data, isLoading, error } = useAgentHealthScore(agentId, period, range);

  return (
    <div
      className="rounded-lg border p-6"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {isLoading && (
        <div
          className="flex items-center gap-3 text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          <FiActivity className="h-5 w-5 animate-pulse" />
          Carregando saúde do agente...
        </div>
      )}

      {error && (
        <div
          className="text-sm"
          style={{ color: '#ef4444' }}
        >
          Não foi possível carregar os dados de saúde do agente.
        </div>
      )}

      {!isLoading && !error && data && !data.hasEvaluators && (
        <div className="text-center py-4">
          <div
            className="text-lg font-semibold mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Ainda não há dados suficientes
          </div>
          <p
            className="text-sm"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Execute algumas conversas com este agente para começar a ver o
            diagnóstico. As métricas são coletadas automaticamente após cada
            execução.
          </p>
        </div>
      )}

      {!isLoading && !error && data && data.hasEvaluators && (
        <HealthScoreHeroContent
          score={data.overallScore}
          traceCount={data.traceCount}
        />
      )}
    </div>
  );
};

interface HealthScoreHeroContentProps {
  score: number;
  traceCount: number;
}

const HealthScoreHeroContent: React.FC<HealthScoreHeroContentProps> = ({
  score,
  traceCount,
}) => {
  const interpretation = interpret(score);
  const scorePercent = Math.round(score * 100);

  return (
    <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
      {/* Score gigante */}
      <div className="flex items-center gap-4">
        <div style={{ color: interpretation.color }}>{interpretation.icon}</div>
        <div>
          <div
            className="text-5xl font-bold leading-none"
            style={{ color: interpretation.color }}
          >
            {scorePercent}%
          </div>
          <div
            className="text-xs mt-1"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Baseado em {traceCount}{' '}
            {traceCount === 1 ? 'conversa avaliada' : 'conversas avaliadas'}
          </div>
        </div>
      </div>

      {/* Interpretação leiga */}
      <div className="flex-1">
        <div
          className="text-lg font-semibold mb-1"
          style={{ color: 'var(--theme-card-text)' }}
        >
          {interpretation.label}
        </div>
        <p
          className="text-sm leading-relaxed"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {interpretation.description}
        </p>
      </div>
    </div>
  );
};
